import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { UserPlus, X } from 'lucide-react';
import clsx from 'clsx';
import type { ShiftInstance, ShiftAssignment } from '../../services/api';
import { AssignedEmployeeBadge } from './EmployeeCard';

// BOH = Back of House (Kitchen)
const BOH_ROLES = new Set(['cook', 'dishwasher']);

interface ShiftSlotProps {
  shift: ShiftInstance;
  staffingSummary: Map<string, { required: number; assigned: number }>;
  onRemoveAssignment: (assignmentId: string) => void;
  onShiftClick?: () => void;
  employees: Array<{ id: string; firstName: string; lastName: string; primaryRole: string }>;
  certificationWarning?: boolean;
  certificationWarningMessage?: string;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  highlightEmployeeId?: string | null;
  onQuickAssign?: (employeeId: string, role: string) => Promise<void>;
  availableEmployees?: Array<{ id: string; firstName: string; lastName: string; primaryRole: string }>;
}

export default function ShiftSlot({
  shift,
  staffingSummary,
  onRemoveAssignment,
  onShiftClick,
  employees,
  certificationWarning,
  certificationWarningMessage,
  isHighlighted,
  isDimmed,
  highlightEmployeeId,
  onQuickAssign,
  availableEmployees,
}: ShiftSlotProps) {
  const [showQuickAssign, setShowQuickAssign] = useState(false);
  const [isAssigning, setIsAssigning] = useState<string | null>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: `shift-${shift.id}`,
    data: {
      type: 'shift',
      shiftId: shift.id,
      shift,
    },
  });

  // Format time
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  };

  // Get shift color or default
  const shiftColor = shift.color || '#6366F1';

  // Calculate totals
  const totalRequired = Array.from(staffingSummary.values()).reduce(
    (sum, { required }) => sum + required,
    0
  );
  const totalAssigned = (shift.assignments || []).length;
  const isFilled = totalAssigned >= totalRequired;
  const needsMore = totalRequired - totalAssigned;

  // Get missing roles
  const missingRoles: string[] = [];
  staffingSummary.forEach((value, role) => {
    if (value.assigned < value.required) {
      missingRoles.push(role);
    }
  });

  // Get employees available for quick assign (not already assigned to this shift)
  const assignedIds = new Set((shift.assignments || []).map(a => a.employeeId));
  const availableCandidates = (availableEmployees || []).filter(emp => !assignedIds.has(emp.id));
  const kitchenCandidates = availableCandidates.filter(emp => BOH_ROLES.has(emp.primaryRole)).slice(0, 4);
  const floorCandidates = availableCandidates.filter(emp => !BOH_ROLES.has(emp.primaryRole)).slice(0, 4);

  // Handle quick assign click
  const handleQuickAssign = async (emp: { id: string; primaryRole: string }) => {
    if (!onQuickAssign) return;
    setIsAssigning(emp.id);
    try {
      await onQuickAssign(emp.id, emp.primaryRole);
      setShowQuickAssign(false);
    } finally {
      setIsAssigning(null);
    }
  };

  // Get assignments with employee details
  const assignmentsWithDetails = (shift.assignments || []).map((a) => {
    const emp = employees.find((e) => e.id === a.employeeId);
    return {
      ...a,
      firstName: emp?.firstName || 'Unknown',
      lastName: emp?.lastName || '',
      role: a.role,
    };
  });

  // Convert hex to rgba for light tint
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const bgTint = hexToRgba(shiftColor, 0.08);
  const borderTint = hexToRgba(shiftColor, 0.3);

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'rounded-lg border transition-all',
        isOver
          ? 'border-indigo-400 bg-indigo-50 shadow-md'
          : isHighlighted
          ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
          : isDimmed
          ? 'border-slate-200 bg-slate-50 opacity-40'
          : 'hover:shadow-sm'
      )}
      style={!isOver && !isHighlighted && !isDimmed ? {
        backgroundColor: bgTint,
        borderColor: borderTint
      } : undefined}
    >
      {/* Shift header */}
      <button
        onClick={onShiftClick}
        className="w-full flex items-center justify-between gap-2 px-2.5 md:px-3 py-1.5 md:py-2.5 hover:bg-white/50 transition-colors text-left"
      >
        <div className="min-w-0 flex-1">
          <span className="font-medium text-slate-700 text-xs md:text-sm leading-tight">{shift.name}</span>
          <div className="text-[10px] md:text-xs text-slate-400 mt-0.5">
            {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
          </div>
        </div>
        {/* Role coverage chip — only when the shift declares role requirements */}
        {totalRequired > 0 && (
          <span
            className={clsx(
              'flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
              isFilled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
            )}
            title={
              isFilled
                ? 'Fully staffed'
                : `Needs ${needsMore} more${missingRoles.length ? ` · missing: ${missingRoles.join(', ')}` : ''}`
            }
          >
            {totalAssigned}/{totalRequired}
          </span>
        )}
      </button>

      {/* Missing-roles line (only when understaffed) */}
      {totalRequired > 0 && !isFilled && missingRoles.length > 0 && (
        <div className="px-2.5 -mt-1 pb-1 text-[10px] text-amber-600 capitalize leading-tight">
          Missing: {missingRoles.join(', ')}
        </div>
      )}

      {/* Event notes */}
      {shift.notes && (
        <div className="px-2.5 py-1.5 text-[11px] text-slate-500 border-t border-slate-100/50 bg-white/30">
          <span className="leading-snug line-clamp-2">{shift.notes}</span>
        </div>
      )}

      {/* Assigned employees */}
      <div className="px-2.5 py-2 min-h-[40px]">
        {assignmentsWithDetails.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {assignmentsWithDetails.map((assignment) => (
              <AssignedEmployeeBadge
                key={assignment.id}
                employee={assignment}
                onRemove={() => onRemoveAssignment(assignment.id)}
                isHighlighted={highlightEmployeeId === assignment.employeeId}
              />
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-300 text-center py-1">No staff assigned</div>
        )}
      </div>

      {/* Quick assign */}
      {onQuickAssign && !isFilled && (
        <div className="border-t border-slate-100/50">
          {!showQuickAssign ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowQuickAssign(true);
              }}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 md:py-1.5 text-xs text-slate-400 hover:text-indigo-500 active:bg-white/50 transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Add Staff</span>
            </button>
          ) : (
            <div className="px-2.5 py-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-slate-500">Assign to shift</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowQuickAssign(false);
                  }}
                  className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-2">
                {kitchenCandidates.length > 0 && (
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1">Kitchen</div>
                    <div className="flex flex-wrap gap-1.5">
                      {kitchenCandidates.map((emp) => (
                        <button
                          key={emp.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAssign(emp);
                          }}
                          disabled={isAssigning === emp.id}
                          className="px-2.5 py-1.5 text-xs text-slate-600 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 rounded-md border border-amber-200 transition-colors"
                        >
                          {emp.firstName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {floorCandidates.length > 0 && (
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1">Floor</div>
                    <div className="flex flex-wrap gap-1.5">
                      {floorCandidates.map((emp) => (
                        <button
                          key={emp.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAssign(emp);
                          }}
                          disabled={isAssigning === emp.id}
                          className="px-2.5 py-1.5 text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 active:bg-slate-200 rounded-md border border-slate-200 transition-colors"
                        >
                          {emp.firstName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {kitchenCandidates.length === 0 && floorCandidates.length === 0 && (
                  <span className="text-xs text-slate-400">No available staff</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drop indicator */}
      {isOver && (
        <div className="px-3 py-1.5 text-xs text-slate-500 text-center border-t border-slate-100">
          drop here
        </div>
      )}
    </div>
  );
}
